import {
  useState,
  useEffect,
  useRef,
  ChangeEvent,
  useMemo,
  FormEvent
} from "react"
import { Input } from "../components/ui/input"
import { Button } from "../components/ui/button"
import {
  FileText,
  User,
  X,
  Search,
  Grid,
  Settings,
  LayoutDashboard,
  Truck,
  Loader
} from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover"

import { fetcher, handleMutation, handleUpdates } from "../util/axiosConfig"
import requestErrorHandler from "../util/requestErrorHandler"
import useSWR from "swr"
import useSWRMutation from "swr/mutation"
import { Checkbox, CheckboxProps, message } from "antd"
import Switch from "./Switch"
import BreadCrumb from "./BreadCrumb"
import { SYSTEM_PERMISSIONS } from "../util/constants"
// import useAuth from "../hooks/useAuth"

type FormData = {
  name: string;
  email: string;
  phone: string;
  permissions: string[];
  isSuperAdmin: boolean;
}

interface AddNewAdminProps {
  setView?: (view: number) => void;
  admin: FederatedAdmin;
  onSave?: () => void;
}

export default function AddNewAdmin({ setView, onSave , admin }: AddNewAdminProps) {
  // const { user } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    name: admin?.name ?? '',
    email: admin?.email ??  '',
    phone: admin?.phone ?? '',
    permissions: admin?.permissions ?? [],
    isSuperAdmin: admin?.isSuperAdmin ?? false
  });
  const [selectedAccounts, setSelectedAccounts] = useState<FleetOwner[]>([])
  const [open, setOpen] = useState(false)
  const [toggle, setToggle] = useState(admin?.accessibleFleetAccounts.length > 0)
  const [searchValue, setSearchValue] = useState("")
  const inputRef = useRef<any>(null)

  const { data: fleetOwners, isLoading } = useSWR('newAdmin/fleet-owners/all', fetcher);
  const { trigger, isMutating } = useSWRMutation("/newAdmin/create-normal-admin", handleMutation);
  const { trigger: triggerResetPassword, isMutating: isResettingPassword } = useSWRMutation("/newAdmin/reset-password", handleMutation);
  const { trigger: triggerUpdates, isMutating: isUpdating } = useSWRMutation("/newAdmin/edit-admin", handleUpdates);

  const allAccounts: FleetOwner[] = useMemo(() => {
    const truckers = fleetOwners?.data ?? [];
    // Find all accessible accounts 
    if (admin && admin?.accessibleFleetAccounts) {
      const accessibleAccount: FleetOwner[] = [];
      admin.accessibleFleetAccounts.forEach(accId => {
        truckers.forEach((trucker: FleetOwner) => {
          if (trucker.id === accId) {
            accessibleAccount.push({...trucker})
          }
        });
      })
      setSelectedAccounts(accessibleAccount)
    }
    return truckers;
  }, [fleetOwners, isLoading]);

  const filteredAccounts = useMemo(() => {
    return allAccounts.filter((acc) => acc.companyName.toLowerCase().includes(searchValue.toLowerCase()) && !selectedAccounts.some((selected) => selected.id === acc.id));
  }, [searchValue, allAccounts]);

  // const checkAll = selectedAccounts.length === filteredAccounts.length;
  const indeterminate = selectedAccounts.length > 0 && selectedAccounts.length < filteredAccounts.length;


  // Focus input when popover opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef?.current && inputRef?.current?.focus();
      }, 100)
    }
  }, [open])

  const handleSelectAccount = (account: any) => {
    if (!selectedAccounts.some((selected) => selected.id === account.id)) {
      setSelectedAccounts([...selectedAccounts, account])
    } else {
      setSelectedAccounts(selectedAccounts.filter((selected) => selected.id !== account.id));
    }
  }

  const onCheckAllChange: CheckboxProps['onChange'] = (e) => {
    setSelectedAccounts(e.target.checked ? filteredAccounts : []);
  };

  const isChecked = (id: string) => {
    if (selectedAccounts.some((selected) => selected.id === id)) {
      return true;
    }
    return false;
  } 

  const removeAccount = (accountId: string) => {
    setSelectedAccounts(selectedAccounts.filter((account) => account.id !== accountId))
  }

  const handleInputChange  = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value
    })
  }

  const handleSetPermissions  = (permission: string) => {
    const rights = ['read', 'write', 'delete'];
    const permissions = formData.permissions;
    const isExisting = permissions.includes(permission);

    const [module, right] = permission.split('-');

    if (isExisting) {
      // Construct permissions to Remove
      const rightPos = rights.length - rights.indexOf(right);
      const modulePermissions = rights.slice(-rightPos).map(r => `${module}-${r}`);
      // Create a Set of items to remove for fast lookup
      const toRemove = new Set(modulePermissions);
      // Filter permissions keeping only items not in toRemove
      const updatePermissions = permissions.filter(p => !toRemove.has(p));

      setFormData({
        ...formData,
        permissions: [...updatePermissions]
      })
      return
    }

    // Construct permissions to Add
    const rightPos = rights.indexOf(right);
    const modulePermissions = rights.slice(0, rightPos + 1).map(r => `${module}-${r}`);

    const updatePermissions = [...permissions, ...modulePermissions];
    setFormData({
      ...formData,
      permissions: [...updatePermissions]
    })

  }

  const resetPassword = async () => {
    try {
      const res = await triggerResetPassword({
        adminEmail: formData.email
      })

      if (res) {
        message.success(res.message || "Password reset email sent successfully!");
      }
    } catch (error) {
      requestErrorHandler(error)
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault()

      if (!admin) {
        await trigger({
          ...formData,
          accessibleFleetAccounts: selectedAccounts.map(acc => acc.id),
        })
        message.success("Admins Created Successfully!")
        setView?.(1);
        onSave?.();
        return;
      }

      let perms = [];
      if (admin.permissions.length > 0) {
        const merged = formData.permissions.concat(admin.permissions).filter((item, idx, arr) => arr.indexOf(item) === idx)
        perms = merged;
      } else {
        perms = formData.permissions
      }

      try {
        triggerUpdates({
          ...formData,
          id: admin.id,
          permissions: [...perms],
          accessibleFleetAccounts: selectedAccounts.map(acc => acc.id),
        })
      } catch (error) {
        requestErrorHandler(error)
      }
      
      message.success("Admins Updated Successfully!")
      setView?.(1);
      onSave?.();
    } catch (error) {
      requestErrorHandler(error)
    }
  }

  return (
    <main className="flex-1 p-6">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-sm p-8">
        <BreadCrumb label="Add New Admin" showBack onPressBack={()=> setView?.(1)}/>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="space-y-6">
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <label htmlFor="name" className="text-gray-700 font-medium">
                  Name
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  defaultValue={admin?.name}
                  onChange={handleInputChange}
                  className="bg-gray-200 border-l-4 border-blue-600"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="email" className="text-gray-700 font-medium">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  defaultValue={admin?.email}
                  onChange={handleInputChange}
                  className="bg-gray-200 border-l-4 border-blue-600"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="email" className="text-gray-700 font-medium">
                  Phone Number
                </label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  defaultValue={admin?.phone}
                  onChange={handleInputChange}
                  className="bg-gray-200 border-l-4 border-blue-600"
                />
              </div>

              <div>
                <Checkbox
                  checked={formData.isSuperAdmin}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      isSuperAdmin: e.target.checked,
                      permissions: e.target.checked ? Object.values(SYSTEM_PERMISSIONS).flat() : []
                    });
                    onCheckAllChange(e);
                  }
                }>
                  Super Admin (Has access to all accounts and permissions)
                </Checkbox>
              </div>

              <div>
                <Button
                  variant="link"
                  className="pl-0 font-semibold text-red-400"
                  onClick={(e) => {
                  e.preventDefault();
                  resetPassword()
                }}>
                  Initiate Password Reset
                  { isResettingPassword && <Loader className="animate-spin" /> }
                </Button>
              </div>

              <div className="space-y-4 pt-4">
                <h3 className="text-gray-700 font-medium">Accounts</h3>
                <div className="flex items-center space-x-2">
                 <Switch
                    setSwitchValue={setToggle}
                    switchValue={toggle}
                  />
                  <label
                    htmlFor="accessible-accounts"
                    className="text-blue-600 font-medium text-sm cursor-pointer"
                  >
                    Add Accessible Accounts
                  </label>
                </div>

               {toggle && <div className="space-y-4">
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <div className="relative flex items-center gap-2">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search accounts..."
                          className="pl-10"
                          disabled={isLoading}
                          readOnly
                          value={searchValue}
                          onClick={() => setOpen(true)}
                        />
                        {isLoading && <Loader className="animate-spin" color="gray" />}
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="bg-white z-50 w-[var(--radix-popover-trigger-width)] p-2" align="start">
                      <div className="space-y-2">           
                        <div className="relative flex items-center gap-2">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            ref={inputRef}
                            placeholder="Search accounts..."
                            className="pl-10 !border-1 !border-gray-400"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                          />
                        </div>                      
                        <div className="max-h-[200px] overflow-y-auto">
                          <div className="px-2 py-1.5 text-sm cursor-pointer hover:bg-blue-50 rounded">
                            <Checkbox indeterminate={indeterminate} onChange={onCheckAllChange}>
                              Check all
                            </Checkbox>
                          </div>
                          {filteredAccounts.length > 0 ? (
                            filteredAccounts.map((account) => (
                              <div
                                key={account.id}
                                className="px-2 py-1.5 text-sm cursor-pointer hover:bg-blue-50 rounded flex items-center gap-2"
                                onClick={() => handleSelectAccount(account)}
                              >
                                <Checkbox checked={isChecked(account.id)} />
                                {account.companyName}
                              </div>
                            ))
                          ) : (
                            <div className="px-2 py-1.5 text-sm text-gray-500">No accounts found</div>
                          )}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  {/* Display Selected FleetOwner Accounts */}
                  <div className="flex flex-wrap gap-2">
                    {selectedAccounts.map((account) => (
                      <div
                        key={account.id}
                        className="border border-blue-400 rounded px-3 py-1.5 text-sm text-blue-700 bg-blue-50 flex items-center gap-1"
                      >
                        {account.companyName}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0 hover:bg-transparent"
                          onClick={() => removeAccount(account.id)}
                        >
                          <X className="h-3 w-3" />
                          <span className="sr-only">Remove</span>
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>}
              </div>
              <Button
                type="submit"
                disabled={
                  !formData.email ||
                  !formData.name ||
                  !formData.phone ||
                  formData.permissions.length < 1
                }
                className="bg-blue-600 hover:bg-blue-700 text-white w-full mt-8"
              >
                { (isMutating || isUpdating)
                  ? <Loader className="animate-spin" />
                  : admin
                    ? "Save changes"
                    : "Save"
                }
              </Button>
            </form>
          </div>
          <PermissionsSection
            permissions={formData.permissions}
            setPermission={handleSetPermissions}
          />
        </div>
      </div>
    </main>

  )
}

const MODULES = [
  {
    name: 'dashboard',
    icon: <LayoutDashboard color="black" />
  },
  {
    name: 'requests',
    icon: <FileText color="black" />
  },
  {
    name: 'trucks',
    icon: <Truck color="black" />
  },
  {
    name: 'zones',
    icon: <Grid color="black" />
  },
  {
    name: 'users',
    icon: <User color="black" />
  },
  {
    name: 'settings',
    icon: <Settings color="black" />
  }
]

interface PermissionsSectionProps {
  permissions: string[],
  setPermission: (value: string) => void
}

export function PermissionsSection({ permissions, setPermission }: PermissionsSectionProps) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-medium">Permissions</h2>

      <div className="space-y-4">
        <h3 className="text-gray-700 font-medium">Modules</h3>

        <div className="space-y-4">
          {MODULES.map((module) => (
            <ModulePermission
              key={module.name}
              icon={module.icon}
              module={module.name}
              permissions={permissions}
              onChange={setPermission}
            />
          ))}
        </div>

        <div className="flex items-center gap-4">
          <div className="w-6 flex-shrink-0">
            <User className="h-5 w-5 text-gray-600 opacity-0" />
          </div>
          <div className="w-full h-8 rounded-sm overflow-hidden flex">
            <div className={`h-full w-1/3 bg-transparent flex justify-center items-center`}>
              READ
            </div>
            <div className={`h-full w-1/3 bg-transparent flex justify-center items-center`}>
              WRITE
            </div>
            <div className={`h-full w-1/3 bg-transparent flex justify-center items-center`}>
              DELETE
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ModulePermissionProps {
  icon: React.ReactNode,
  module: string,
  permissions: string[],
  onChange: (permission: string) => void
}


export function ModulePermission({
  icon,
  module,
  permissions,
  onChange
}: ModulePermissionProps){

  const handleToggle = (right: string) => {
    const permission = `${module}-${right}`;
    onChange(permission);
  };

  const isActive = (right: string) => {
    const permission = `${module}-${right}`;
    return permissions.includes(permission);
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-14 flex flex-col items-center">
        {icon}
        <span className="capitalize text-xs font-semibold text-gray-500">{module}</span>
      </div>
      <div className="flex-1 flex w-full h-8 bg-gray-100 rounded-sm overflow-hidden">
        {['read', 'write', 'delete'].map((right) => (
          <div
            key={right}
            className={`read h-full w-1/3 flex justify-center items-center cursor-pointer ${isActive(right) ? "bg-blue-600 text-white" : "bg-gray-200"
              }`}
              onClick={() => handleToggle(right)}
          />))}
      </div>
    </div>
  );
}

